/**
 * Workspaces service: one login, many books (an accountant working for clients),
 * the clients overview, and the review verdicts on tax reports.
 */

import { api, unwrapApiResponse } from '@/platform/api/api.service'
import { authService } from '@/platform/auth/services/auth.service'

export type WorkspaceKind = 'own' | 'client'
export type WorkspaceRole = 'read' | 'review' | 'manage'
export type ReviewVerdict = 'approved' | 'changes-requested'

export interface Workspace {
  tenantId: string
  name: string
  kind: WorkspaceKind
  role: string
  grantId: string | null
  expiresAt: string | null
}

export interface WorkspacesResponse {
  current: string
  workspaces: Workspace[]
}

export interface EnterWorkspaceResponse {
  tokens: { accessToken: string; tokenType: string; expiresIn: number }
  workspace: Workspace
}

export interface OverviewReport {
  id: string
  year: number
  status: string
  submitted: boolean
  review: ReviewVerdict | null
  reviewCurrent: boolean
}

export interface OverviewClient {
  tenantId: string
  name: string
  role: string
  transactions: number
  reports: OverviewReport[]
}

export interface ClientsOverview {
  clients: OverviewClient[]
}

export interface ReportReview {
  id: string
  runId: string
  reviewerId: string
  verdict: ReviewVerdict
  note: string | null
  inputsHash: string
  createdAt: string
  current: boolean
}

export interface ReviewsResponse {
  items: ReportReview[]
}

/** The switcher is only worth showing when there is somewhere else to go. */
export function hasMultipleWorkspaces(workspaces: readonly Workspace[] | undefined): boolean {
  return (workspaces?.length ?? 0) > 1
}

/** The workspace the session currently points at, if it is in the list. */
export function currentWorkspaceOf(data: WorkspacesResponse | undefined): Workspace | undefined {
  return data?.workspaces.find((w) => w.tenantId === data.current)
}

/** Latest report of a tax year (reports arrive in any order). */
export function latestReportsByYear(reports: readonly OverviewReport[]): OverviewReport[] {
  const byYear = new Map<number, OverviewReport>()
  for (const report of reports) {
    if (!byYear.has(report.year)) byYear.set(report.year, report)
  }
  return [...byYear.values()].sort((a, b) => b.year - a.year)
}

export type ReviewBadge = 'approved' | 'changes-requested' | 'stale' | 'none'

export function reviewBadgeOf(report: OverviewReport): ReviewBadge {
  if (report.review === null) return 'none'
  if (!report.reviewCurrent) return 'stale'
  return report.review
}

export const workspacesService = {
  async list(): Promise<WorkspacesResponse> {
    return unwrapApiResponse(await api.get<WorkspacesResponse>('/api/workspaces'))
  },

  async overview(): Promise<ClientsOverview> {
    return unwrapApiResponse(await api.get<ClientsOverview>('/api/workspaces/overview'))
  },

  /** Enter a workspace: swap the stored access token and reload from the root. */
  async enter(tenantId: string): Promise<EnterWorkspaceResponse> {
    const result = unwrapApiResponse(
      await api.post<EnterWorkspaceResponse>(
        `/api/workspaces/${encodeURIComponent(tenantId)}/enter`,
      ),
    )
    authService.adoptAccessToken(result.tokens.accessToken)
    return result
  },

  async listReviews(reportId: string): Promise<ReviewsResponse> {
    return unwrapApiResponse(
      await api.get<ReviewsResponse>(`/api/tax-reports/${encodeURIComponent(reportId)}/reviews`),
    )
  },

  async submitReview(
    reportId: string,
    review: { verdict: ReviewVerdict; note?: string },
  ): Promise<ReportReview> {
    return unwrapApiResponse(
      await api.post<ReportReview>(
        `/api/tax-reports/${encodeURIComponent(reportId)}/reviews`,
        review,
      ),
    )
  },
}
